import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { tenantService, type TenantDto } from '@/services/tenants';
import {
  superadminBillingService,
  type BillingStreamItem,
  type BillingStreamStudentOption,
} from '@/services/superadminBilling';
import { Activity, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';

const KIND_LABEL: Record<string, string> = {
  db_change: 'DB change',
  payment_webhook: 'Payment webhook',
  credit_ledger: 'Credits',
  refund_request: 'Refund',
};

const KIND_CLASS: Record<string, string> = {
  db_change: 'bg-violet-100 text-violet-800',
  payment_webhook: 'bg-amber-100 text-amber-900',
  credit_ledger: 'bg-emerald-100 text-emerald-900',
  refund_request: 'bg-sky-100 text-sky-900',
};

function formatJson(value: unknown): string {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const StreamRow: React.FC<{ item: BillingStreamItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const hasDetail =
    item.stateBefore != null ||
    item.stateAfter != null ||
    item.rawWebhookPayload != null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-start gap-2"
        onClick={() => hasDetail && setOpen((v) => !v)}
        disabled={!hasDetail}
      >
        {hasDetail ? (
          open ? (
            <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          ) : (
            <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
          )
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${KIND_CLASS[item.kind] ?? 'bg-slate-100'}`}
            >
              {KIND_LABEL[item.kind] ?? item.kind}
            </span>
            <span className="text-sm font-medium text-foreground truncate">{item.title}</span>
          </div>
          <p className="text-xs text-slate-500">{formatWhen(item.occurredAt)}</p>
          {(item.studentDisplayName || item.teacherDisplayName) && (
            <p className="text-xs text-slate-600">
              {item.studentDisplayName && <>Student: {item.studentDisplayName}</>}
              {item.studentDisplayName && item.teacherDisplayName && ' · '}
              {item.teacherDisplayName && <>Teacher: {item.teacherDisplayName}</>}
            </p>
          )}
          {item.summary && <p className="text-xs text-slate-600">{item.summary}</p>}
          {item.paymentWebhookEventId && item.kind === 'db_change' && (
            <p className="text-[10px] text-slate-400 font-mono">
              webhook {item.paymentWebhookEventId.slice(0, 8)}…
            </p>
          )}
          {item.kind === 'payment_webhook' && (
            <p className="text-[10px] text-slate-500">
              {item.paymentEventName}
              {item.signatureValid === false && ' · invalid signature'}
              {item.processError && ` · ${item.processError}`}
            </p>
          )}
        </div>
      </button>
      {open && hasDetail && (
        <div className="border-t border-slate-100 bg-slate-50/80 px-4 py-3 space-y-3 text-xs">
          {item.stateBefore != null && (
            <div>
              <p className="font-semibold text-slate-700 mb-1">Before</p>
              <pre className="overflow-x-auto rounded bg-white border p-2 font-mono text-[11px] max-h-64">
                {formatJson(item.stateBefore)}
              </pre>
            </div>
          )}
          {item.stateAfter != null && (
            <div>
              <p className="font-semibold text-slate-700 mb-1">
                {item.stateBefore != null ? 'After' : 'Details'}
              </p>
              <pre className="overflow-x-auto rounded bg-white border p-2 font-mono text-[11px] max-h-64">
                {formatJson(item.stateAfter)}
              </pre>
            </div>
          )}
          {item.rawWebhookPayload != null && (
            <div>
              <p className="font-semibold text-slate-700 mb-1">Raw webhook payload</p>
              <pre className="overflow-x-auto rounded bg-white border p-2 font-mono text-[11px] max-h-80">
                {formatJson(item.rawWebhookPayload)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SuperadminBillingActivityPage: React.FC = () => {
  const { toast } = useToast();
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId')
  );
  const [selectedTenant, setSelectedTenant] = useState<TenantDto | null>(null);
  const [students, setStudents] = useState<BillingStreamStudentOption[]>([]);
  const [studentFilter, setStudentFilter] = useState<string>('');
  const [items, setItems] = useState<BillingStreamItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tenantId = sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId');
    setSelectedTenantId(tenantId);
  }, []);

  useEffect(() => {
    const loadTenant = async () => {
      if (!selectedTenantId) {
        setSelectedTenant(null);
        return;
      }
      try {
        setSelectedTenant(await tenantService.getTenant(selectedTenantId));
      } catch {
        setSelectedTenant(null);
      }
    };
    void loadTenant();
  }, [selectedTenantId]);

  const loadStudents = useCallback(async () => {
    if (!selectedTenantId) return;
    try {
      setStudents(await superadminBillingService.listBillingStudents(selectedTenantId));
    } catch {
      setStudents([]);
    }
  }, [selectedTenantId]);

  const loadStream = useCallback(async () => {
    if (!selectedTenantId) return;
    try {
      setLoading(true);
      const result = await superadminBillingService.getBillingStream(selectedTenantId, {
        studentAuthUserId: studentFilter || undefined,
        limit: 150,
      });
      setItems(result.items);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load billing activity stream',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, studentFilter, toast]);

  useEffect(() => {
    void loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    void loadStream();
  }, [loadStream]);

  return (
    <DashboardLayout>
      <Helmet>
        <title>Billing activity · Arrangelly</title>
      </Helmet>
      <div className="space-y-6 p-4 md:p-6 max-w-5xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Billing activity stream
            </CardTitle>
            <CardDescription>
              Per-tenant timeline of payment webhooks, database changes (before/after), credits, and
              refunds. Select a tenant on{' '}
              <Link to="/tenants" className="text-violet-600 hover:underline">
                Tenants
              </Link>{' '}
              first, then filter by student.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedTenantId ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                No tenant selected. Open <Link to="/tenants">Tenants</Link> and pick a tenant to
                scope this stream.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                Tenant: <strong>{selectedTenant?.name ?? selectedTenantId}</strong>
              </p>
            )}

            <div className="flex flex-wrap items-end gap-3">
              <label className="text-sm space-y-1 block">
                <span className="text-slate-600">Student</span>
                <select
                  className="block min-w-[200px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                  value={studentFilter}
                  onChange={(e) => setStudentFilter(e.target.value)}
                  disabled={!selectedTenantId}
                >
                  <option value="">All students</option>
                  {students.map((s) => (
                    <option key={s.studentAuthUserId} value={s.studentAuthUserId}>
                      {s.displayName}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!selectedTenantId || loading}
                onClick={() => void loadStream()}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {loading && items.length === 0 ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-slate-500">
                No events yet for this scope. New Rapyd/payment webhooks will log DB before/after states
                after migration 028 is applied.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <StreamRow key={`${item.kind}-${item.id}`} item={item} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SuperadminBillingActivityPage;
